import React, { useState, useRef, useEffect } from 'react';
import './ChatBot.scss';

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'bot';
  timestamp: Date;
}

const ChatBot: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      text: "Hi! I'm Shadow Expert, your AI assistant for Shadow Tracker. How can I help you today?",
      sender: 'bot',
      timestamp: new Date()
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const getBotResponse = (userMessage: string): string => {
    const message = userMessage.toLowerCase();
    
    // Fleet size specific responses (1-300 vehicles)
    if (message.match(/\b(\d+)\s*(vehicle|truck|car|van|fleet)/)) {
      const number = parseInt(message.match(/\b(\d+)/)?.[1] || '0');
      if (number >= 100) {
        return `${number} vehicles - that's a massive fleet! For that scale, you'll definitely want Starlink for your daily-use vehicles. Professional installation, centralized fleet management, driver monitoring - essential for operations that size. We can handle fleets up to 300+ vehicles. Want me to connect you with our enterprise team?`;
      } else if (number >= 50) {
        return `${number} vehicles is a substantial fleet. Starlink is perfect for your daily-use work vehicles - gives you proper fleet management, driver behavior monitoring, and centralized control. For that many vehicles, the professional installation and tamper-proof design really pays off.`;
      } else if (number >= 20) {
        return `${number} vehicles - solid fleet size! Starlink would be ideal for your daily-use work vehicles. You'll get fleet dashboard, driver monitoring, route optimization, plus professional installation for all ${number} vehicles. Much better than managing individual trackers.`;
      } else if (number >= 10) {
        return `${number} vehicles is getting into proper fleet territory. For daily-use work vehicles, Starlink gives you way better management tools - driver behavior, route tracking, centralized dashboard. Professional installation for all ${number} vehicles included.`;
      } else if (number >= 5) {
        return `For ${number} work vehicles, Starlink would give you professional fleet features - driver monitoring, route optimization, tamper-proof installation. Perfect if these are daily-use vehicles that need proper management.`;
      } else if (number >= 2) {
        return `For ${number} vehicles, it depends on usage. If they're daily-use work vehicles, Starlink gives you proper fleet management. If they're occasional-use or personal vehicles, Shadow trackers might be perfect at $${number * 299} total.`;
      } else {
        return `For 1 vehicle, it depends how you use it. Daily work vehicle? Starlink gives you professional features. Personal car or something you don't use often? Shadow at $299 is perfect.`;
      }
    }
    
    // Usage-based recommendations
    if (message.includes('daily') || message.includes('everyday') || message.includes('regular use') || message.includes('work vehicle')) {
      return "For daily-use vehicles, Starlink is your best bet. It's designed for vehicles that are constantly on the road - gives you driver behavior monitoring, route optimization, and professional fleet management. Perfect for work vehicles that get heavy use.";
    }
    
    if (message.includes('occasional') || message.includes('weekend') || message.includes('not often') || message.includes('rarely use')) {
      return "For vehicles you don't use often, Shadow is perfect! 2-year battery means it just sits there waiting until you need it. No point paying for professional fleet features on a weekend car or boat that sits in storage most of the time.";
    }
    
    // Fleet and business questions
    if (message.includes('fleet') || message.includes('business') || message.includes('work') || message.includes('company')) {
      if (message.includes('starlink') || message.includes('hardwired') || message.includes('professional')) {
        return "Starlink is built for daily-use fleet vehicles - hardwired installation, tamper-proof, driver behavior monitoring. Perfect for work vehicles that are on the road every day and need proper management.";
      }
      if (message.includes('trip') || message.includes('information') || message.includes('monitoring')) {
        return "Sounds like you need Starlink for those daily-use work vehicles. It gives you detailed trip reports, driver behavior monitoring, route history - all the professional fleet stuff for vehicles in constant use.";
      }
      return "For work fleets, it depends on usage. Daily-use vehicles need Starlink for proper fleet management. Occasional-use vehicles or equipment can use Shadow trackers. What's your vehicle usage like?";
    }
    
    // Starlink specific questions
    if (message.includes('starlink')) {
      if (message.includes('price') || message.includes('cost')) {
        return "Starlink pricing depends on fleet size and features needed. It's more than Shadow at $299, but you get professional installation, hardwired security, and advanced fleet management for daily-use vehicles. Want me to connect you with our fleet specialist?";
      }
      if (message.includes('install') || message.includes('hardwired')) {
        return "Starlink requires professional installation - it's hardwired into your vehicle's electrical system. Takes about an hour per vehicle, but then it's completely tamper-proof and invisible. Perfect for daily-use work vehicles.";
      }
      if (message.includes('difference') || message.includes('vs') || message.includes('compare')) {
        return "Shadow ($299) is for occasional-use vehicles, boats, bikes - anything that sits around a lot. Starlink is for daily-use work vehicles that need professional fleet management, driver monitoring, and tamper-proof installation.";
      }
      return "Starlink is designed for daily-use fleet vehicles - hardwired installation, driver behavior monitoring, route optimization, and tamper-proof design. Perfect for work vehicles that are constantly on the road.";
    }
    
    // Driver behavior and monitoring
    if (message.includes('driver') || message.includes('behavior') || message.includes('monitoring') || message.includes('speeding')) {
      return "That's where Starlink excels - monitors driver behavior like speeding, harsh braking, rapid acceleration. Essential for daily-use fleet vehicles where you need to track how company vehicles are being driven.";
    }
    
    // Boat and motorbike specific questions
    if (message.includes('boat') || message.includes('motorbike') || message.includes('motorcycle')) {
      if (message.includes('2') || message.includes('4') || message.includes('multiple')) {
        return "Perfect! Boats and bikes can't be fitted with Starlink since there's no proper electrical system to hardwire into. Shadow is ideal - magnetic mount, weatherproof, and perfect for assets that don't get daily use. You'd want 4 Shadow trackers total at $299 each.";
      }
      return "Shadow is perfect for boats and motorbikes! They can't be fitted with Starlink trackers since there's no electrical system to hardwire into. Plus, boats and bikes usually don't get daily use, so Shadow's 2-year battery is ideal.";
    }
    
    // Asset vs vehicle distinction
    if (message.includes('asset') || message.includes('equipment') || message.includes('trailer') || message.includes('machinery')) {
      return "For assets and equipment that don't get daily use, Shadow is perfect. 2-year battery, magnetic mount, weatherproof design. Starlink is really designed for daily-use vehicles with proper electrical systems.";
    }
    
    // Multiple vehicles
    if ((message.includes('2') || message.includes('4') || message.includes('multiple') || message.includes('several')) && 
        (message.includes('vehicle') || message.includes('car') || message.includes('bike') || message.includes('boat'))) {
      return "Depends on usage! Daily-use work vehicles should get Starlink for proper fleet management. Occasional-use vehicles, boats, or bikes are perfect for Shadow trackers. What's the usage pattern like?";
    }
    
    // Price questions - varied responses
    if (message.includes('price') || message.includes('cost') || message.includes('how much')) {
      if (message.includes('expensive') || message.includes('cheap')) {
        return "Shadow is $299 for occasional-use vehicles and assets - great for things that sit around most of the time. Starlink costs more but gives you professional fleet management for daily-use work vehicles. Different tools for different usage patterns.";
      }
      return "Shadow is $299 for occasional-use vehicles and assets. Starlink has fleet pricing for daily-use work vehicles with professional features. Depends on how often you use the vehicles.";
    }
    
    if (message.includes('buy') || message.includes('order') || message.includes('purchase')) {
      return "You can order Shadow trackers directly from our website for occasional-use vehicles. For Starlink fleet solutions for daily-use vehicles, I can connect you with our business team. Which usage pattern fits your needs?";
    }
    
    // Battery questions - contextual
    if (message.includes('battery') || message.includes('charge')) {
      if (message.includes('how long') || message.includes('last')) {
        return "Shadow lasts two full years without charging - perfect for vehicles you don't use daily. Starlink is hardwired so it runs off your vehicle's electrical system, ideal for daily-use vehicles.";
      }
      return "Shadow's 2-year battery is perfect for occasional-use vehicles. Starlink being hardwired is better for daily-use vehicles since there's no battery to worry about.";
    }
    
    // Installation questions
    if (message.includes('install') || message.includes('setup') || message.includes('hard to')) {
      return "Shadow: magnetic mount, stick it on and done - perfect for boats, bikes, occasional-use vehicles. Starlink: professional hardwired installation for daily-use work vehicles that need tamper-proof tracking.";
    }
    
    // Vehicle compatibility
    if (message.includes('car') || message.includes('vehicle') || message.includes('work with')) {
      return "Shadow works on anything metal - cars, bikes, boats, trailers, equipment. Perfect for occasional-use assets. Starlink works on daily-use vehicles with electrical systems - cars, trucks, vans that are constantly on the road.";
    }
    
    // Greetings - more natural
    if (message.includes('hello') || message.includes('hi') || message.includes('hey')) {
      return "Hey there! What can I help you with? Looking at tracking for occasional-use vehicles/assets or daily-use fleet management?";
    }
    
    // Thanks
    if (message.includes('thank') || message.includes('thanks')) {
      return "No worries! Anything else about Shadow for occasional-use vehicles or Starlink for daily-use fleets?";
    }
    
    // Default - more conversational
    return "I'm not sure I caught that. Are you asking about occasional-use vehicles (Shadow) or daily-use fleet management (Starlink)? Happy to help with either!";
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim()) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      text: inputValue,
      sender: 'user',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsTyping(true);

    // Simulate typing delay
    setTimeout(() => {
      const botResponse: Message = {
        id: (Date.now() + 1).toString(),
        text: getBotResponse(inputValue),
        sender: 'bot',
        timestamp: new Date()
      };
      
      setMessages(prev => [...prev, botResponse]);
      setIsTyping(false);
    }, 1500);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSendMessage();
    }
  };

  return (
    <div className="chatbot-container">
      {isOpen && (
        <div className="chatbot-window">
          <div className="chatbot-header">
            <div className="chatbot-title">
              <span className="amazon-logo">🤖</span>
              <span>Shadow Expert</span>
            </div>
            <button 
              className="close-btn"
              onClick={() => setIsOpen(false)}
            >
              ×
            </button>
          </div>
          
          <div className="chatbot-messages">
            {messages.map((message) => (
              <div key={message.id} className={`message ${message.sender}`}>
                <div className="message-content">
                  <p>{message.text}</p>
                  <span className="timestamp">
                    {message.timestamp.toLocaleTimeString('en-NZ', { 
                      hour: '2-digit', 
                      minute: '2-digit' 
                    })}
                  </span>
                </div>
              </div>
            ))}
            
            {isTyping && (
              <div className="message bot typing">
                <div className="message-content">
                  <div className="typing-indicator">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
          
          <div className="chat-input">
            <input 
              type="text" 
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Ask me anything about Shadow Tracker..."
              className="message-input"
            />
            <button 
              className="send-btn"
              onClick={handleSendMessage}
              disabled={!inputValue.trim()}
            >
              →
            </button>
          </div>
        </div>
      )}
      
      <button 
        className="chatbot-toggle"
        onClick={() => setIsOpen(!isOpen)}
      >
        {isOpen ? '×' : '💬'}
      </button>
    </div>
  );
};

export default ChatBot;
